import type { PaginationPropSize, HotKeys } from 'uikit';
import { Text, cnMixSpace, Select, Pagination, Layout } from 'uikit';

import cn from 'classnames';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import Styles from './CustomPagination.module.css';

interface IPageSizeSelect {
    currentPage: number;
    setCurrentPage: (newPage: number) => void;
    totalPages: number;
    currentPageSize?: number;
    setCurrentPageSize?: (newSize: number) => void;
    selectItems?: IPageSize[];
    size?: PaginationPropSize;
    className?: string;
    position?: 'start' | 'center' | 'end' | 'space-between';
    hotkeys?: HotKeys;
}

interface IPageSize {
    value: number;
    label: string;
    id: number;
}

export const CustomPagination: FC<IPageSizeSelect> = ({
    currentPage,
    setCurrentPage,
    totalPages,
    currentPageSize,
    setCurrentPageSize,
    selectItems,
    size = 's',
    position = 'end',
    className,
    hotkeys,
}) => {
    const [sizeValue, setSizeValue] = useState<IPageSize>({
        value: currentPageSize || 0,
        label: `${currentPageSize}`,
        id: currentPageSize || 0,
    });

    const defaultItems = useMemo(
        () => [
            { value: 50, label: '50', id: 1 },
            { value: 100, label: '100', id: 2 },
            { value: 150, label: '150', id: 3 },
            { value: 200, label: '200', id: 4 },
            { value: 250, label: '250', id: 5 },
            { value: 500, label: '500', id: 6 },
        ],
        [],
    );

    const handleChangeSize = useCallback(
        ({ value: selectedValue }: { value: IPageSize | null }) => {
            if (!selectedValue) {
                return;
            }
            const newSize = selectedValue?.value;

            if (setCurrentPageSize && newSize) {
                setSizeValue(selectedValue);
                setCurrentPageSize(newSize);
            }
        },
        [setCurrentPageSize],
    );

    return (
        <div
            className={cn(Styles.paginationBlock, className, {
                [Styles.positionPageStart]: position === 'start',
                [Styles.positionPageCenter]: position === 'center',
                [Styles.positionPageEnd]: position === 'end',
                [Styles.positionPageSpaceBetween]: position === 'space-between',
            })}
        >
            {!!currentPageSize && !!totalPages && (
                <Layout style={{ alignItems: 'center', whiteSpace: 'nowrap' }}>
                    <Text as="span" size="s">
                        Отобразить по
                    </Text>
                    <Select
                        items={selectItems || defaultItems}
                        value={sizeValue}
                        size="s"
                        onChange={handleChangeSize}
                        className={Styles.perPageSelect}
                    />
                    <Text
                        as="span"
                        size="s"
                        className={cnMixSpace({
                            mR: 'm',
                        })}
                    >
                        записей на одной странице
                    </Text>
                </Layout>
            )}
            <Layout>
                <Pagination
                    currentPage={currentPage}
                    onChange={setCurrentPage}
                    totalPages={totalPages}
                    size={size}
                    className={Styles.pagination}
                    hotkeys={hotkeys}
                />
            </Layout>
        </div>
    );
};

CustomPagination.displayName = 'CustomPagination';
